import { useCallback, useEffect, useRef, useState } from "react";
import katex from "katex";
import { MIME_TYPES, randomId } from "@excalidraw/common";
import {
  convertToExcalidrawElements,
  newElementWith,
} from "@excalidraw/element";
import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import DialogActionButton from "@excalidraw/excalidraw/components/DialogActionButton";

import "katex/dist/katex.min.css";

import type {
  ExcalidrawElement,
  ExcalidrawImageElement,
  FileId,
} from "@excalidraw/element/types";
import type {
  BinaryFileData,
  DataURL,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";

import "./EquationDialog.scss";

import { getCustomToolShortcutLabel } from "./customToolShortcuts";

import type { EquationDraftUpdate } from "../data";

const EQUATION_DATA_KEY = "excalimathEquation";

export type EquationState = {
  version: 1;
  latex: string;
};

type EquationPoint = { x: number; y: number };

type EquationKey = {
  label: string;
  latex: string;
  previewLatex?: string;
};

const equationKey = (
  label: string,
  latex = label,
  previewLatex?: string,
): EquationKey => ({
  label,
  latex,
  previewLatex,
});

const EQUATION_KEY_GROUPS = [
  {
    id: "excalimath-structures",
    label: "Forms",
    keys: [
      equationKey("fraction", "\\frac{#?}{#?}", "\\frac{a}{b}"),
      equationKey("power", "^{#?}", "x^n"),
      equationKey("subscript", "_{#?}", "x_n"),
      equationKey("square root", "\\sqrt{#?}", "\\sqrt{x}"),
      equationKey("nth root", "\\sqrt[#?]{#?}", "\\sqrt[n]{x}"),
      equationKey("absolute value", "\\left|#?\\right|", "\\left|x\\right|"),
      equationKey("parentheses", "\\left(#?\\right)", "\\left(x\\right)"),
      equationKey("square brackets", "\\left[#?\\right]", "\\left[x\\right]"),
      equationKey("braces", "\\left\\{#?\\right\\}", "\\left\\{x\\right\\}"),
      equationKey("+"),
      equationKey("−", "-"),
      equationKey("×", "\\times"),
      equationKey("÷", "\\div"),
      equationKey("="),
      equationKey("integral", "\\int_{#?}^{#?}#?\\,dx", "\\int_a^b"),
      equationKey("limit", "\\lim_{#?\\to#?}#?", "\\lim_{x\\to a}"),
      equationKey("sum", "\\sum_{#?}^{#?}#?", "\\sum_{i=1}^{n}"),
      equationKey("product", "\\prod_{#?}^{#?}#?", "\\prod_{i=1}^{n}"),
    ],
  },
  {
    id: "excalimath-relations",
    label: "Symbols",
    keys: [
      equationKey("∞", "\\infty"),
      equationKey("∅", "\\varnothing"),
      equationKey("∠", "\\angle"),
      equationKey("⊥", "\\perp"),
      equationKey("△", "\\triangle"),
      equationKey("∼", "\\sim"),
      equationKey("≠", "\\neq"),
      equationKey("→", "\\rightarrow"),
      equationKey("←", "\\leftarrow"),
      equationKey("↔", "\\leftrightarrow"),
      equationKey("⇒", "\\Rightarrow"),
      equationKey("⇐", "\\Leftarrow"),
      equationKey("⇔", "\\Leftrightarrow"),
      equationKey("∀", "\\forall"),
      equationKey("∃", "\\exists"),
      equationKey("∪", "\\cup"),
      equationKey("∩", "\\cap"),
      equationKey("∈", "\\in"),
      equationKey("∉", "\\notin"),
      equationKey("⊂", "\\subset"),
      equationKey("⊆", "\\subseteq"),
      equationKey("<"),
      equationKey(">"),
      equationKey("≤", "\\le"),
      equationKey("≥", "\\ge"),
      equationKey("≈", "\\approx"),
      equationKey("≡", "\\equiv"),
    ],
  },
  {
    id: "excalimath-greek",
    label: "Greek",
    keys: [
      equationKey("α", "\\alpha"),
      equationKey("β", "\\beta"),
      equationKey("γ", "\\gamma"),
      equationKey("δ", "\\delta"),
      equationKey("ε", "\\epsilon"),
      equationKey("ζ", "\\zeta"),
      equationKey("η", "\\eta"),
      equationKey("θ", "\\theta"),
      equationKey("ι", "\\iota"),
      equationKey("κ", "\\kappa"),
      equationKey("λ", "\\lambda"),
      equationKey("μ", "\\mu"),
      equationKey("ν", "\\nu"),
      equationKey("ξ", "\\xi"),
      equationKey("ο", "o"),
      equationKey("π", "\\pi"),
      equationKey("ρ", "\\rho"),
      equationKey("σ", "\\sigma"),
      equationKey("τ", "\\tau"),
      equationKey("υ", "\\upsilon"),
      equationKey("φ", "\\phi"),
      equationKey("χ", "\\chi"),
      equationKey("ψ", "\\psi"),
      equationKey("ω", "\\omega"),
      equationKey("Γ", "\\Gamma"),
      equationKey("Δ", "\\Delta"),
      equationKey("Θ", "\\Theta"),
      equationKey("Λ", "\\Lambda"),
      equationKey("Ξ", "\\Xi"),
      equationKey("Π", "\\Pi"),
      equationKey("Σ", "\\Sigma"),
      equationKey("Φ", "\\Phi"),
      equationKey("Ψ", "\\Psi"),
      equationKey("Ω", "\\Omega"),
    ],
  },
] as const;

export const getEquationState = (element: ExcalidrawElement | undefined) => {
  const value = element?.customData?.[EQUATION_DATA_KEY];
  if (!value || typeof value !== "object") {
    return null;
  }
  return value as EquationState;
};

export const EquationIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M5 5h13M8 5l4 7-4 7h10"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const EquationToolbarButton = ({
  onClick,
  active,
}: {
  onClick: () => void;
  active: boolean;
}) => (
  <button
    className="GraphToolbarButton"
    type="button"
    onClick={onClick}
    title={`Place a math equation — ${getCustomToolShortcutLabel("equation")}`}
    aria-label="Place a math equation"
    aria-keyshortcuts={getCustomToolShortcutLabel("equation")}
    aria-pressed={active}
    data-active={active}
  >
    <EquationIcon />
    <span>Equation</span>
    <kbd>{getCustomToolShortcutLabel("equation")}</kbd>
  </button>
);

const makeEquationSvg = async (latex: string, ownerDocument: Document) => {
  const rendered = katex.renderToString(latex, {
    displayMode: true,
    output: "mathml",
    throwOnError: true,
  });
  const measurement = ownerDocument.createElement("div");
  measurement.className = "EquationDialog__measurement";
  measurement.innerHTML = rendered;
  ownerDocument.body.appendChild(measurement);
  const bounds = measurement.getBoundingClientRect();
  const math = measurement.querySelector("math")?.outerHTML;
  measurement.remove();

  if (!math) {
    throw new Error("The equation could not be rendered as MathML.");
  }

  const width = Math.max(96, Math.ceil(bounds.width) + 32);
  const height = Math.max(64, Math.ceil(bounds.height) + 24);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><foreignObject width="100%" height="100%"><div xmlns="http://www.w3.org/1999/xhtml" style="box-sizing:border-box;color:#1b1b1f;display:flex;align-items:center;justify-content:center;font-size:40px;height:100%;padding:12px 16px;white-space:nowrap">${math}</div></foreignObject></svg>`;

  return {
    width,
    height,
    dataURL: `data:${MIME_TYPES.svg},${encodeURIComponent(svg)}` as DataURL,
  };
};

export const EquationDialog = ({
  api,
  editingElementId,
  insertionPoint,
  sessionId,
  onDraftChange,
  onClose,
}: {
  api: ExcalidrawImperativeAPI;
  editingElementId: string | null;
  insertionPoint: EquationPoint | null;
  sessionId: string;
  onDraftChange: (update: EquationDraftUpdate) => void;
  onClose: () => void;
}) => {
  const editingElement = editingElementId
    ? api.getSceneElements().find((element) => element.id === editingElementId)
    : undefined;
  const savedState = getEquationState(editingElement);
  const [latex, setLatex] = useState(savedState?.latex ?? "");
  const [activeKeyGroup, setActiveKeyGroup] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const hostRef = useRef<HTMLDivElement>(null);
  const sourceRef = useRef<HTMLInputElement>(null);
  const renderSequenceRef = useRef(0);

  const close = useCallback(() => {
    renderSequenceRef.current += 1;
    onDraftChange({ action: "clear", sessionId });
    onClose();
  }, [onClose, onDraftChange, sessionId]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return;
    }

    katex.render(latex || "\\phantom{x}", host, {
      displayMode: true,
      throwOnError: false,
    });
  }, [latex]);

  useEffect(() => {
    const host = hostRef.current;
    const ownerWindow = host?.ownerDocument.defaultView;
    const value = latex.trim();
    if (!ownerWindow) {
      return;
    }

    if (!value) {
      renderSequenceRef.current += 1;
      onDraftChange({ action: "clear", sessionId });
      return;
    }

    const timeout = ownerWindow.setTimeout(() => {
      const sequence = ++renderSequenceRef.current;
      void makeEquationSvg(value, host.ownerDocument)
        .then((rendered) => {
          if (sequence !== renderSequenceRef.current) {
            return;
          }

          const currentElement = editingElementId
            ? api
                .getSceneElements()
                .find((element) => element.id === editingElementId)
            : undefined;
          const point = insertionPoint ?? { x: 0, y: 0 };
          const center = currentElement
            ? {
                x: currentElement.x + currentElement.width / 2,
                y: currentElement.y + currentElement.height / 2,
              }
            : point;

          onDraftChange({
            action: "update",
            draft: {
              sessionId,
              elementId: currentElement?.id ?? null,
              x: center.x - rendered.width / 2,
              y: center.y - rendered.height / 2,
              width: rendered.width,
              height: rendered.height,
              dataURL: rendered.dataURL,
              mask: currentElement
                ? {
                    x: currentElement.x,
                    y: currentElement.y,
                    width: currentElement.width,
                    height: currentElement.height,
                  }
                : null,
            },
          });
        })
        .catch((previewError) => {
          setError(
            previewError instanceof Error
              ? previewError.message
              : "The live equation preview could not be updated.",
          );
        });
    }, 200);

    return () => ownerWindow.clearTimeout(timeout);
  }, [api, editingElementId, insertionPoint, latex, onDraftChange, sessionId]);

  const insertLatex = (value: string) => {
    const source = sourceRef.current;
    const start = source?.selectionStart ?? latex.length;
    const end = source?.selectionEnd ?? start;
    const firstPlaceholder = value.indexOf("#?");
    const inserted = value.replaceAll("#?", "");
    const cursorPosition =
      start + (firstPlaceholder === -1 ? inserted.length : firstPlaceholder);
    const nextValue = `${latex.slice(0, start)}${inserted}${latex.slice(end)}`;
    setLatex(nextValue);
    source?.ownerDocument.defaultView?.setTimeout(() => {
      source?.focus();
      source?.setSelectionRange(cursorPosition, cursorPosition);
    }, 0);
  };

  const saveEquation = async () => {
    const value = latex.trim();
    if (!value) {
      setError("Enter an equation.");
      return;
    }

    setIsSaving(true);
    try {
      const host = hostRef.current;
      if (!host) {
        return;
      }

      const rendered = await makeEquationSvg(value, host.ownerDocument);
      const fileId = randomId() as FileId;
      const now = Date.now();
      const file: BinaryFileData = {
        id: fileId,
        mimeType: MIME_TYPES.svg,
        dataURL: rendered.dataURL,
        created: now,
        lastRetrieved: now,
      };
      const equationState: EquationState = { version: 1, latex: value };
      api.addFiles([file]);

      const currentElement = editingElementId
        ? api
            .getSceneElementsIncludingDeleted()
            .find((element) => element.id === editingElementId)
        : undefined;

      if (currentElement?.type === "image") {
        const updated = newElementWith(
          currentElement as ExcalidrawImageElement,
          {
            fileId,
            status: "pending",
            x: currentElement.x + (currentElement.width - rendered.width) / 2,
            y: currentElement.y + (currentElement.height - rendered.height) / 2,
            width: rendered.width,
            height: rendered.height,
            customData: {
              ...currentElement.customData,
              [EQUATION_DATA_KEY]: equationState,
            },
          },
        );
        api.updateScene({
          elements: api
            .getSceneElementsIncludingDeleted()
            .map((element) => (element.id === updated.id ? updated : element)),
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });
      } else {
        const point = insertionPoint ?? { x: 0, y: 0 };
        const [element] = convertToExcalidrawElements(
          [
            {
              type: "image",
              x: point.x - rendered.width / 2,
              y: point.y - rendered.height / 2,
              width: rendered.width,
              height: rendered.height,
              fileId,
              status: "pending",
              customData: { [EQUATION_DATA_KEY]: equationState },
            },
          ],
          { regenerateIds: true },
        );
        api.updateScene({
          elements: [...api.getSceneElementsIncludingDeleted(), element],
          appState: { selectedElementIds: { [element.id]: true } },
          captureUpdate: CaptureUpdateAction.IMMEDIATELY,
        });
      }

      onDraftChange({ action: "clear", sessionId });
      onClose();
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "The equation cannot be inserted.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      title={editingElement ? "Edit equation" : "Insert equation"}
      onCloseRequest={close}
      className="EquationDialog"
      size="wide"
    >
      <p className="EquationDialog__hint">
        Type with your keyboard or select symbols from the math keyboard.
      </p>
      <div ref={hostRef} className="EquationDialog__fieldHost">
        <div className="EquationDialog__loading">Preparing preview…</div>
      </div>
      <div className="EquationDialog__keyboard" aria-label="Math symbols">
        <div className="EquationDialog__keyboardTabs" role="tablist">
          {EQUATION_KEY_GROUPS.map((group, index) => (
            <button
              key={group.id}
              type="button"
              role="tab"
              aria-selected={activeKeyGroup === index}
              onClick={() => setActiveKeyGroup(index)}
            >
              {group.label}
            </button>
          ))}
        </div>
        <div className="EquationDialog__keys">
          {EQUATION_KEY_GROUPS[activeKeyGroup].keys.map((item) => (
            <button
              key={`${item.label}-${item.latex}`}
              type="button"
              title={`Insert ${item.label}`}
              aria-label={`Insert ${item.label}`}
              onClick={() => insertLatex(item.latex)}
              dangerouslySetInnerHTML={{
                __html: katex.renderToString(item.previewLatex ?? item.latex, {
                  throwOnError: false,
                }),
              }}
            />
          ))}
        </div>
      </div>
      <label className="EquationDialog__source">
        <span>LaTeX source</span>
        <input
          ref={sourceRef}
          value={latex}
          onChange={(event) => {
            setLatex(event.target.value);
            setError(null);
          }}
          spellCheck={false}
          autoComplete="off"
          autoFocus
          onKeyDown={(event) => event.stopPropagation()}
          onKeyUp={(event) => event.stopPropagation()}
        />
      </label>
      {error && <div className="EquationDialog__error">{error}</div>}
      <div className="EquationDialog__footer">
        <span>Double-click an equation on the board to edit it later.</span>
        <div className="EquationDialog__actions">
          <DialogActionButton label="Cancel" onClick={close} />
          <DialogActionButton
            label={editingElement ? "Update equation" : "Insert equation"}
            actionType="primary"
            onClick={saveEquation}
            disabled={isSaving}
            isLoading={isSaving}
          >
            <EquationIcon size={18} />
          </DialogActionButton>
        </div>
      </div>
    </Dialog>
  );
};

export const equationTestUtils = {
  getEquationState,
  makeEquationSvg,
};
